// src/config/centralAuth.ts
// Central Identity OIDC configuration for the BeHeard web client.
//
// This file is a thin app-specific wrapper around @levelup/central-auth.
// It reads env vars, sets APP_ID, and re-exports shared helpers
// pre-bound with BeHeard's configuration.
//
// IMPORTANT: This app uses OIDC + PKCE (S256) for authentication.
// Frontend calls Central directly - no backend proxy for login.

import {
  buildOidcEndpoints,
  buildBearerEndpoints,
  buildSwitchContextUrl,
  buildOidcLogoutUrl as _buildOidcLogoutUrl,
  initiateCentralLogin as _initiateCentralLogin,
  initiateCentralLogout as _initiateCentralLogout,
  PKCE_VERIFIER_KEY,
  OAUTH_STATE_KEY,
  OAUTH_RETURN_PATH_KEY,
} from '@levelup/central-auth';
import { sanitizeReturnPath } from '../services/oidc/returnPath';

import type { AuthMode, CentralRole } from '@levelup/central-auth';

// Re-export shared constants for backward compatibility
export { PKCE_VERIFIER_KEY, OAUTH_STATE_KEY, OAUTH_RETURN_PATH_KEY };

// Re-export shared types for backward compatibility
export type { AuthMode, CentralRole };
export type {
  CentralUser,
  CentralWorkspace,
  CentralAuthResponse,
  CentralSelectWorkspaceResponse,
  SuiteApp,
  SuiteAppExtended,
  SwitchContextResponse,
  SwitchContextWorkspace,
  SwitchContextMeta,
  SwitchContextUser,
  BootStatus,
  CapabilitiesMap,
  WorkspacePermissions,
} from '@levelup/central-auth';

// ============================================================
// App-specific Configuration (reads env vars)
// ============================================================

/**
 * Application identifier for Central Identity.
 * Used as OIDC client_id.
 */
export const APP_ID = import.meta.env.VITE_APP_ID || 'beheard';

/**
 * OIDC client ID (same as APP_ID — kept for backward compatibility).
 */
export const OIDC_CLIENT_ID = APP_ID;

/**
 * Central Identity base URL.
 * Set via VITE_CENTRAL_URL environment variable.
 */
export const CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL || '';

/**
 * OIDC redirect URI after authentication.
 * Central redirects back to /login where the callback handler processes the code exchange.
 */
export const OIDC_REDIRECT_URI =
  import.meta.env.VITE_OIDC_REDIRECT_URI || `${window.location.origin}/login`;

/**
 * Storage key prefix for Central auth tokens.
 * BeHeard uses memory-only tokens (no localStorage persistence for access tokens),
 * but the prefix is still needed for PKCE session cleanup and logout flows.
 */
export const STORAGE_KEY_PREFIX = 'beheard_central';

/**
 * Feature flag to enable Central auth UI components.
 */
export const CENTRAL_AUTH_ENABLED = (() => {
  const enabled = import.meta.env.VITE_CENTRAL_AUTH_ENABLED;
  if (enabled === 'false' || enabled === '0') return false;
  if (enabled === 'true' || enabled === '1') return true;
  return true; // Default: enabled
})();

// ============================================================
// Pre-built Endpoints (from shared builders + CENTRAL_URL)
// ============================================================

export const OIDC_ENDPOINTS = buildOidcEndpoints(CENTRAL_URL);
export const BEARER_ENDPOINTS = buildBearerEndpoints(CENTRAL_URL);
export const SUITE_SWITCH_CONTEXT_URL = buildSwitchContextUrl(CENTRAL_URL);

// ============================================================
// App-specific Helpers
// ============================================================

/**
 * Check if Central auth is configured and available.
 */
export function isCentralAuthConfigured(): boolean {
  return Boolean(CENTRAL_URL) && CENTRAL_AUTH_ENABLED;
}

/**
 * Get the full URL for a Central API endpoint.
 */
export function getCentralUrl(path: string): string {
  if (!CENTRAL_URL) {
    throw new Error('CENTRAL_URL is not configured');
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${CENTRAL_URL}${normalizedPath}`;
}

// ============================================================
// BeHeard-bound Login/Logout (delegates to shared with app config)
// ============================================================

/**
 * Initiate the Central OIDC Authorization Code + PKCE flow.
 * Uses shared PKCE + authorization URL helpers under the hood.
 *
 * `returnPath` is untrusted (it can originate from a `?returnTo=` query
 * parameter), so it is funnelled through sanitizeReturnPath() before it is
 * persisted for the post-login redirect.
 */
export async function initiateCentralLogin(returnPath?: string): Promise<void> {
  // Only a same-origin relative path is ever stored as the post-login target.
  const path =
    sanitizeReturnPath(returnPath || window.location.pathname + window.location.search) ?? '/';

  return _initiateCentralLogin({
    centralUrl: CENTRAL_URL,
    clientId: APP_ID,
    redirectUri: OIDC_REDIRECT_URI,
    scope: 'openid profile email',
    returnPath: path !== '/login' ? path : '/',
    storage: sessionStorage,
  });
}

/**
 * Build the OIDC RP-Initiated Logout URL.
 */
export function buildOidcLogoutUrl(params: {
  centralUrl?: string;
  clientId?: string;
  postLogoutRedirectUri: string;
}): string {
  return _buildOidcLogoutUrl({
    centralUrl: params.centralUrl || CENTRAL_URL,
    clientId: params.clientId || APP_ID,
    postLogoutRedirectUri: params.postLogoutRedirectUri,
  });
}

/**
 * Initiate Central OIDC RP-Initiated Logout.
 */
export function initiateCentralLogout(postLogoutRedirectUri?: string): void {
  const redirectUri = postLogoutRedirectUri || `${window.location.origin}/login`;

  _initiateCentralLogout({
    centralUrl: CENTRAL_URL,
    clientId: APP_ID,
    postLogoutRedirectUri: redirectUri,
    storageKeyPrefix: STORAGE_KEY_PREFIX,
  });
}

// ============================================================
// App-specific Types (not in shared package)
// ============================================================

/**
 * Normalized suite app with a guaranteed `id` field.
 * App-specific — used by normalizeApps utility.
 */
export interface NormalizedSuiteApp {
  id: string;
  displayName: string;
  webUrl: string;
  iconUrl?: string | null;
  mobileScheme?: string | null;
  status: string;
  order: number;
}
